// 匯出我的資料 (DSAR, spec 20 §6) — POST /v3/me/export → poll status → short-lived download URL.
// Feature-flag aware: first 403 FEATURE_NOT_AVAILABLE hides the action and shows 「即將開放」.
// The presigned download URL is never logged or cached (13 §5 PII rule) — used once, transiently.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { formatBytes, formatTimestampUs } from '../../lib/format';

const SCOPES: { value: string; labelKey: string }[] = [
  { value: 'profile', labelKey: 'account.scopeProfile' },
  { value: 'devices', labelKey: 'account.scopeDevices' },
  { value: 'subscriptions', labelKey: 'account.scopeSubscriptions' },
  { value: 'billing', labelKey: 'account.scopeBilling' },
  { value: 'events', labelKey: 'account.scopeEvents' },
  { value: 'recordings_index', labelKey: 'account.scopeRecordingsIndex' },
];

const EXPORT_ID_KEY = 'ls.account.export_request_id'; // request id only — never tokens/URLs

interface ExportStatus {
  export_request_id: string;
  status: 'pending' | 'processing' | 'ready' | 'expired' | 'failed';
  requested_at?: number;
  ready_at?: number;
  expires_at?: number | string;
  size_bytes?: number;
  download_available?: boolean;
}

export default function DataExportCard() {
  const { t } = useTranslation();
  const [scopes, setScopes] = useState<string[]>(SCOPES.map((s) => s.value));
  const [format, setFormat] = useState<'json' | 'csv'>('json');
  const [request, setRequest] = useState<ExportStatus | null>(null);
  const [featureBlocked, setFeatureBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  const fetchStatus = useCallback(async (id: string) => {
    try {
      const res = await apiFetch<ExportStatus>(`/v3/me/export/${id}`);
      setRequest(res.data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        localStorage.removeItem(EXPORT_ID_KEY);
        setRequest(null);
      } else if (e instanceof ApiError && e.code === 'FEATURE_NOT_AVAILABLE') {
        setFeatureBlocked(true);
      }
      // other errors: keep current state; next poll retries
    }
  }, []);

  // restore an in-flight export across reloads
  useEffect(() => {
    const saved = localStorage.getItem(EXPORT_ID_KEY);
    if (saved) void fetchStatus(saved);
  }, [fetchStatus]);

  // foreground polling every 30s while pending/processing (20 §6.2; no push in v1)
  useEffect(() => {
    if (pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    if (request && (request.status === 'pending' || request.status === 'processing')) {
      pollTimer.current = window.setInterval(() => {
        void fetchStatus(request.export_request_id);
      }, 30_000);
    }
    return () => {
      if (pollTimer.current !== null) window.clearInterval(pollTimer.current);
    };
  }, [request, fetchStatus]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<ExportStatus>('/v3/me/export', {
        method: 'POST',
        body: { scope: scopes, format },
        idempotencyKey: crypto.randomUUID(), // failed → retry must use a NEW key (20 §6.2)
      });
      setRequest(res.data);
      localStorage.setItem(EXPORT_ID_KEY, res.data.export_request_id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'FEATURE_NOT_AVAILABLE') {
        setFeatureBlocked(true);
      } else if (e instanceof ApiError && e.code === 'RATE_LIMITED') {
        setError(t('account.errRateLimitedFrequent'));
      } else {
        setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.submitFailedNetwork'));
      }
    } finally {
      setBusy(false);
    }
  }, [scopes, format, t]);

  const download = useCallback(async () => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ download_url: string }>(`/v3/me/export/${request.export_request_id}/download`);
      const a = document.createElement('a');
      a.href = res.data.download_url;
      a.rel = 'noopener';
      a.click();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'EXPORT_EXPIRED') {
        setRequest((r) => (r ? { ...r, status: 'expired' } : r));
        setError(t('account.exportLinkExpired'));
      } else {
        setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('account.downloadFailedRetry'));
      }
    } finally {
      setBusy(false);
    }
  }, [request, t]);

  const toggleScope = (value: string) => {
    setScopes((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const inFlight = request && (request.status === 'pending' || request.status === 'processing');

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.exportTitle')}</h2>

      {featureBlocked ? (
        <p className="text-secondary">{t('account.comingSoonDot')}</p>
      ) : (
        <>
          {!request && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-5)' }}>
                {SCOPES.map((s) => (
                  <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <input
                      type="checkbox"
                      style={{ minHeight: 'auto' }}
                      checked={scopes.includes(s.value)}
                      onChange={() => toggleScope(s.value)}
                      disabled={busy}
                    />
                    {t(s.labelKey)}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
                <span className="text-secondary">{t('account.format')}</span>
                <select aria-label={t('account.exportFormat')} value={format} onChange={(e) => setFormat(e.target.value as 'json' | 'csv')} disabled={busy}>
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
              <div>
                <button className="btn-primary" disabled={busy || scopes.length === 0} onClick={() => void start()}>
                  {busy ? t('common.submitting') : t('account.startExport')}
                </button>
              </div>
            </div>
          )}

          {inFlight && (
            <p className="text-secondary">
              {t('account.exportPreparing')}{' '}
              <button className="btn-ghost" onClick={() => void fetchStatus(request.export_request_id)}>
                {t('account.checkNow')}
              </button>
            </p>
          )}

          {request?.status === 'ready' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <p style={{ margin: 0 }}>
                {t('account.exportReady', { size: formatBytes(request.size_bytes) })}
                <span className="text-secondary">
                  {' '}
                  {t('account.exportLinkValidUntil', { until: typeof request.expires_at === 'number' ? formatTimestampUs(request.expires_at) : '—' })}
                </span>
              </p>
              <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
                <button className="btn-primary" disabled={busy} onClick={() => void download()}>
                  {t('account.download')}
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    localStorage.removeItem(EXPORT_ID_KEY);
                    setRequest(null);
                  }}
                >
                  {t('account.exportAgain')}
                </button>
              </div>
            </div>
          )}

          {(request?.status === 'expired' || request?.status === 'failed') && (
            <div>
              <p className="error-text">
                {request.status === 'expired' ? t('account.exportFileExpired') : t('account.exportFailed')}{t('account.pleaseExportAgain')}
              </p>
              <button
                className="btn-ghost"
                onClick={() => {
                  localStorage.removeItem(EXPORT_ID_KEY);
                  setRequest(null);
                  setError(null);
                }}
              >
                {t('account.exportAgain')}
              </button>
            </div>
          )}

          {error && <p className="error-text">{error}</p>}
        </>
      )}
    </div>
  );
}
